import type { OptionType } from '../refinement-bar/types'

import { useEffect, useRef, useState } from 'react'
import { Command as CommandPrimitive } from 'cmdk'
import { SearchIcon, XIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	CommandEmpty,
	CommandGroup,
	CommandItem,
	CommandList,
	CommandLoading,
} from '@/shared/components/ui/command'
import {
	Popover,
	PopoverAnchor,
	PopoverContent,
} from '@/shared/components/ui/popover'
import { Skeleton } from '@/shared/components/ui/skeleton'

import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
} from '../ui/input-group'
import { Spinner } from '../ui/spinner'

export default function AsyncCombobox({
	error,
	className,
	value,
	onSearchChange,
	placeholder,
	setValue,
	disabled = false,
	options,
	isLoading,
	onClear,
}: {
	error?: {
		message?: string
	}
	className?: string
	value: string
	onSearchChange: (value: string) => void
	setValue: (value: string) => void
	placeholder: string
	disabled?: boolean
	options: OptionType[]
	isLoading?: boolean
	onClear?: () => void
}) {
	const { t } = useTranslation()

	const [isOpen, setIsOpen] = useState(false)
	const [searchText, setSearchText] = useState(value || '')
	useEffect(() => {
		setSearchText(value)
	}, [value])
	const contentRef = useRef<HTMLDivElement>(null)
	const onFocusCapture = () => setIsOpen(true)
	const onBlurCapture = () => {
		requestAnimationFrame(() => {
			if (
				contentRef.current &&
				!contentRef.current.contains(document.activeElement)
			) {
				setIsOpen(false)
			}
		})
	}

	return (
		<CommandPrimitive data-slot='combobox' className={className}>
			<Popover open={isOpen}>
				<PopoverAnchor>
					<InputGroup>
						<InputGroupAddon>
							<SearchIcon />
						</InputGroupAddon>
						<CommandPrimitive.Input
							asChild
							className='truncate'
							value={searchText}
							onValueChange={(inputValue) => {
								setSearchText(inputValue)
								onSearchChange(inputValue)
							}}
							placeholder={placeholder}
							disabled={disabled}
							tabIndex={-1}
							onFocusCapture={onFocusCapture}
							onBlurCapture={onBlurCapture}
						>
							<InputGroupInput
								aria-invalid={!!error}
								data-slot='combobox-input'
							/>
						</CommandPrimitive.Input>
						{value && (
							<InputGroupAddon align='inline-end'>
								<InputGroupButton size='icon-xs' onClick={onClear}>
									<XIcon />
								</InputGroupButton>
							</InputGroupAddon>
						)}
						{isLoading && (
							<InputGroupAddon align='inline-end'>
								<Spinner />
							</InputGroupAddon>
						)}
					</InputGroup>
				</PopoverAnchor>
				<PopoverContent
					ref={contentRef}
					className='w-[var(--radix-popover-trigger-width)] p-0'
					onOpenAutoFocus={(e) => e.preventDefault()}
				>
					<CommandList>
						{isLoading ? (
							<CommandLoading className='p-1 text-center text-sm'>
								<Skeleton className='h-8 w-full' />
							</CommandLoading>
						) : (
							<>
								<CommandEmpty>
									{t('app.item_not_exist', {
										defaultValue: 'This item does not exist',
									})}
								</CommandEmpty>
								<CommandGroup className='p-1'>
									{options?.map((option) => (
										<CommandItem
											key={option.value}
											value={option.value}
											keywords={[option.value, option.label]}
											onMouseDown={() => setIsOpen(false)}
											onSelect={() => {
												setValue(option.value)
											}}
										>
											{option.label}
										</CommandItem>
									))}
								</CommandGroup>
							</>
						)}
					</CommandList>
				</PopoverContent>
			</Popover>
		</CommandPrimitive>
	)
}
